import useSWR, { mutate } from "swr";
import { api } from "../../api_client";
import type { SavedComparisonDto } from "../data/comparison.types";

/** Cache key for a single saved comparison, shared with useSavedComparison. */
export function savedComparisonKey(id: string): string {
  return `comparisons/${id}`;
}

/**
 * Whether the current user already saved a comparison of exactly these
 * variants.
 *
 * A refresh wipes the page's "Saved" state, so without this the button offers
 * to save a comparison that is already in "My Comparisons". Hand it the same
 * IDs the save sends, so a hit means the very same row.
 *
 * Written against the axios instance rather than the generated client: the
 * endpoint is new, and `npm run generate:api` has not been run against it yet.
 */
export function useComparisonLookup(variantIds: number[] | null | undefined) {
  // Sorted so the same selection in a different order shares one cache entry.
  const ids = variantIds?.length ? [...variantIds].sort((a, b) => a - b) : [];
  const key =
    ids.length >= 2 ? `comparisons/lookup?variantIds=${ids.join(",")}` : null;

  const swr = useSWR<SavedComparisonDto | null>(
    key,
    async () => {
      const res = await api.get<{ saved: SavedComparisonDto | null }>(
        "/api/v1/comparisons/lookup",
        { params: { variantIds: ids.join(",") } },
      );
      return res.data?.saved ?? null;
    },
    { revalidateOnFocus: false, revalidateOnReconnect: false },
  );

  return {
    /** The saved comparison, or null once we know there is none. */
    data: swr.data ?? null,
    isLoading: Boolean(key) && swr.data === undefined && !swr.error,
    error: swr.error,
  };
}

/**
 * Seed the lookup + single-comparison caches right after a save, so the page
 * can switch to the saved view without a second round trip (and without
 * flashing its skeleton).
 */
export function primeSavedComparisonCache(saved: SavedComparisonDto): void {
  const ids = [...saved.variantIds].sort((a, b) => a - b);
  void mutate(savedComparisonKey(saved.id), saved, false);
  void mutate(`comparisons/lookup?variantIds=${ids.join(",")}`, saved, false);
}
